import React, { Dispatch, SetStateAction } from 'react';
import {
	ToothRestoration,
	ToothRestorationCrownBase,
	ToothRestorationCrownType,
	ToothRestorationImplantType,
	ToothRestorationMaterial,
} from '../../../../graphql/graphql';
import DENTModalOverviewSubButtonsSelection from '../DENTModalOverviewSubButtonsSelection/DENTModalOverviewSubButtonsSelection';
import { RESTORATION_TRANSLATION_MAP } from '../../Helpers/restoration/restorationTranslationMap.helper';

interface Props {
	values?: ToothRestoration;
	setValues: Dispatch<SetStateAction<ToothRestoration | undefined>>;
}

const DENTModalOverviewRestorationCrownButtons = ({
	values,
	setValues,
}: Props) => {
	const handleChangeMaterial = (value: ToothRestorationMaterial) => {
		setValues((prev: ToothRestoration | undefined) => {
			return prev?.material === value
				? {
						material: undefined,
						crownType: undefined,
						crownBase: undefined,
						implantType: undefined,
				  }
				: { ...prev, material: value };
		});
	};

	const handleChangeCrownType = (value: ToothRestorationCrownType) => {
		setValues((prev: ToothRestoration | undefined) => {
			return prev?.crownType === value
				? {
						...prev,
						crownType: undefined,
						crownBase: undefined,
						implantType: undefined,
				  }
				: { ...prev, crownType: value };
		});
	};

	const handleChangeCrownBase = (value: ToothRestorationCrownBase) => {
		setValues((prev: ToothRestoration | undefined) => {
			return prev?.crownBase === value
				? {
						...prev,
						crownBase: undefined,
						implantType: undefined,
				  }
				: { ...prev, crownBase: value };
		});
	};

	const handleChangeImplantType = (value: ToothRestorationImplantType) => {
		setValues((prev: ToothRestoration | undefined) => ({
			...prev,
			implantType: prev?.implantType === value ? undefined : value,
		}));
	};

	const rows = [
		{
			shouldHide: false,
			title:
				'patientTreatment.meddiDENT.dentalCross.restorationModal.material.title',
			items: [
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.restorationModal.material.composite',
					isSelected: values?.material === ToothRestorationMaterial.Composite,
					onClick: () =>
						handleChangeMaterial(ToothRestorationMaterial.Composite),
				},
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.restorationModal.material.ceramic',
					isSelected: values?.material === ToothRestorationMaterial.Ceramic,
					onClick: () => handleChangeMaterial(ToothRestorationMaterial.Ceramic),
				},
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.restorationModal.material.gold',
					isSelected: values?.material === ToothRestorationMaterial.Gold,
					onClick: () => handleChangeMaterial(ToothRestorationMaterial.Gold),
				},
				{
					title:
						RESTORATION_TRANSLATION_MAP[
							ToothRestorationMaterial.NonPreciousMaterial
						],
					isSelected:
						values?.material === ToothRestorationMaterial.NonPreciousMaterial,
					onClick: () =>
						handleChangeMaterial(ToothRestorationMaterial.NonPreciousMaterial),
				},
				{
					title:
						RESTORATION_TRANSLATION_MAP[ToothRestorationMaterial.MetalCeramic],
					isSelected:
						values?.material === ToothRestorationMaterial.MetalCeramic,
					onClick: () =>
						handleChangeMaterial(ToothRestorationMaterial.MetalCeramic),
				},
			],
		},
		{
			shouldHide: !values?.material,
			title:
				'patientTreatment.meddiDENT.dentalCross.restorationModal.crownType.title',
			items: [
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.restorationModal.crownType.singleCrown',
					isSelected:
						values?.crownType === ToothRestorationCrownType.SingleCrown,
					onClick: () =>
						handleChangeCrownType(ToothRestorationCrownType.SingleCrown),
				},
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.restorationModal.crownType.abutment',
					isSelected: values?.crownType === ToothRestorationCrownType.Abutment,
					onClick: () =>
						handleChangeCrownType(ToothRestorationCrownType.Abutment),
				},
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.restorationModal.crownType.pontic',
					isSelected: values?.crownType === ToothRestorationCrownType.Pontic,
					onClick: () =>
						handleChangeCrownType(ToothRestorationCrownType.Pontic),
				},
			],
		},
		{
			shouldHide: !(values?.material && values?.crownType),
			title:
				'patientTreatment.meddiDENT.dentalCross.restorationModal.crownBase.title',
			items: [
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.restorationModal.crownBase.natural',
					isSelected: values?.crownBase === ToothRestorationCrownBase.Natural,
					onClick: () =>
						handleChangeCrownBase(ToothRestorationCrownBase.Natural),
				},
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.restorationModal.crownBase.implant',
					isSelected: values?.crownBase === ToothRestorationCrownBase.Implant,
					onClick: () =>
						handleChangeCrownBase(ToothRestorationCrownBase.Implant),
				},
			],
		},
		{
			shouldHide: !(
				values?.material &&
				values?.crownType &&
				values?.crownBase === ToothRestorationCrownBase.Implant
			),
			title:
				'patientTreatment.meddiDENT.dentalCross.restorationModal.implantType.title',
			items: [
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.restorationModal.implantType.boneLevel',
					isSelected:
						values?.implantType === ToothRestorationImplantType.BoneLevel,
					onClick: () =>
						handleChangeImplantType(ToothRestorationImplantType.BoneLevel),
				},
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.restorationModal.implantType.tissueLevel',
					isSelected:
						values?.implantType === ToothRestorationImplantType.TissueLevel,
					onClick: () =>
						handleChangeImplantType(ToothRestorationImplantType.TissueLevel),
				},
			],
		},
	];

	return <DENTModalOverviewSubButtonsSelection rows={rows} />;
};

export default DENTModalOverviewRestorationCrownButtons;
